import { BaseExportService } from './BaseExportService';

const checkStatusMap = {
  0: 'Failed',
  1: 'Passed',
  2: 'Not Done',
};

/**
 * Comprehensive Liveness Export Service
 * 
 * Handles data export for Comprehensive Liveness Verification reports
 * Combines DHA verification, OCR analysis, and facial biometrics
 */
export class ComprehensiveLivenessExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);
    
    // Extract comprehensive liveness data structure
    this.liveness = this.data?.liveness || {};
    this.ocr = this.data?.ocr || {};
    this.facial_comparison_data = this.data?.facial_comparison_data || {};
    this.dha_ocr_comparison_data = this.data?.DHA_OCR_comparison_data || {};
    this.name_match = this.data?.name_match || {};
  }

  get_filename_prefix() {
    const timestamp = Date.now().toString().slice(-8);
    return `Comprehensive_Liveness_Verification_${timestamp}`;
  }

  get_available_sections() {
    return {
      dha_verification: 'DHA Liveness Verification',
      personal_information: 'Personal Information (OCR)',
      document_details: 'Document Details',
      document_authenticity: 'Document Authentication',
      facial_comparison_selfie_to_doc: 'Facial Comparison (Selfie to Document)',
      facial_comparison_dha_to_doc: 'Facial Comparison (DHA to Document)',
      liveness_quality_assessment: 'Liveness & Quality Assessment',
      name_matching: 'Name Matching Results',
      raw_json: 'Raw JSON Data',
    };
  }

  get_default_export_options() {
    return {
      dha_verification: false,
      personal_information: false,
      document_details: false,
      document_authenticity: false,
      facial_comparison_selfie_to_doc: false,
      facial_comparison_dha_to_doc: false,
      liveness_quality_assessment: false,
      name_matching: false,
      raw_json: false,
    };
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.dha_verification) {
      export_data.dha_verification = this.prepare_dha_verification();
    }

    if (export_options.personal_information) {
      export_data.personal_information = this.prepare_personal_information();
    }

    if (export_options.document_details) {
      export_data.document_details = this.prepare_document_details();
    }

    if (export_options.document_authenticity) {
      export_data.document_authenticity = this.prepare_document_authenticity();
    }

    if (export_options.facial_comparison_selfie_to_doc) {
      export_data.facial_comparison_selfie_to_doc = this.prepare_facial_comparison_selfie_to_doc();
    }

    if (export_options.facial_comparison_dha_to_doc) {
      export_data.facial_comparison_dha_to_doc = this.prepare_facial_comparison_dha_to_doc();
    }

    if (export_options.liveness_quality_assessment) {
      export_data.liveness_quality_assessment = this.prepare_liveness_quality_assessment();
    }

    if (export_options.name_matching) {
      export_data.name_matching = this.prepare_name_matching();
    }

    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  /**
   * Prepare DHA Liveness Verification data
   */
  prepare_dha_verification() {
    const liveness_result = this.liveness.LivenessResult || {};
    const face_result = this.liveness.FaceResult || {};
    const safps_results = this.liveness.SAFPSResults || {};
    
    return [{
      'Tracking Number': this.liveness.TrackingNumber || 'N/A',
      'Status': this.liveness.Status || 'N/A',
      'Message': this.liveness.Message || 'N/A',
      'First Name': this.liveness.FirstName || 'N/A',
      'Last Name': this.liveness.LastName || 'N/A',
      'ID Number': this.liveness.IdNumber || 'N/A',
      'First Name Match': this.liveness.FirstNameResult || 'N/A',
      'Last Name Match': this.liveness.LastNameResult || 'N/A',
      'Face Match': face_result.isIdentical ? 'Yes' : 'No',
      'Face Confidence': face_result.confidence || 'N/A',
      'Liveness Pass': liveness_result.LivenessPassResult ? 'Yes' : 'No',
      'Liveness Score': liveness_result.LivenessResultScore || 'N/A',
      'Liveness Probability': liveness_result.LivenessResultProbability || 'N/A',
      'Liveness Quality': liveness_result.LivenessResultQuality || 'N/A',
      'Liveness Class': liveness_result.LivenessResultClass || 'N/A',
      'Liveness Message': liveness_result.LivenessMessage || 'N/A',
      'Smart Card Issued': this.liveness.SmartCardIssued ? 'Yes' : 'No',
      'ID Issue Date': this.liveness.IDIssueDate || 'N/A',
      'ID Sequence Number': this.liveness.IDSequenceNumber || 'N/A',
      'Dead Indicator': this.liveness.DeadIndicator ? 'Yes' : 'No',
      'Date of Death': this.liveness.DateOfDeath || 'N/A',
      'ID Blocked': this.liveness.IDBlocked ? 'Yes' : 'No',
      'Marital Status': this.liveness.MaritalStatus || 'N/A',
      'Date of Marriage': this.liveness.DateOfMarriage || 'N/A',
      'On HANIS': this.liveness.OnHanis ? 'Yes' : 'No',
      'On NPR': this.liveness.OnNPR ? 'Yes' : 'No',
      'Birth Place Country': this.liveness.BirthPlaceCountry || 'N/A',
      'Birth Place Country Code': this.liveness.BirthPlaceCountryCode || 'N/A',
      'Facial Image Available': this.liveness.FacialImageAvailable ? 'Yes' : 'No',
      'Is Wallet Created': this.liveness.IsWalletCreated ? 'Yes' : 'No',
      'SAFPS Results': safps_results ? JSON.stringify(safps_results) : 'N/A',
      'CRef': this.liveness.CRef || 'N/A',
    }];
  }

  /**
   * Prepare personal information from OCR data
   */
  prepare_personal_information() {
    const doc_details = this.ocr.document_details || {};
    
    return [{
      'Personal Number': doc_details['Personal Number'] || 'N/A',
      'Surname And Given Names': doc_details['Surname And Given Names'] || 'N/A',
      'Date of Birth': doc_details['Date of Birth'] || 'N/A',
      'Place of Birth': doc_details['Place of Birth'] || 'N/A',
      'Sex': doc_details['Sex'] || 'N/A',
      'Age': doc_details['Age'] || 'N/A',
      'Age at Issue': doc_details['Age at Issue'] || 'N/A',
      'Document Number': doc_details['Document Number'] || 'N/A',
      'Serial Number': doc_details['Serial Number'] || 'N/A',
      'Nationality': doc_details['Issuing State Name'] || 'N/A',
      'Country Code': doc_details['Issuing State Code'] || 'N/A',
    }];
  }

  /**
   * Prepare document details
   */
  prepare_document_details() {
    const doc_details = this.ocr.document_details || {};
    
    return [{
      'Document Number': doc_details['Document Number'] || 'N/A',
      'Serial Number': doc_details['Serial Number'] || 'N/A',
      'Date of Issue': doc_details['Date of Issue'] || 'N/A',
      'Date of Expiry': doc_details['Date of Expiry'] || 'N/A',
      'Years Since Issue': doc_details['Years Since Issue'] || 'N/A',
      'Authority': doc_details['Authority'] || 'N/A',
      'Category': doc_details['Category'] || 'N/A',
      'DL Restriction Code': doc_details['DL Restriction Code'] || 'N/A',
      'DL Class Code C1 From': doc_details['DLClassCode C1 From'] || 'N/A',
      'DL Class Code C1 Notes': doc_details['DLClassCode C1 Notes'] || 'N/A',
      'Remainder Term': doc_details['RemainderTerm'] || 'N/A',
      'Issuing State': doc_details['Issuing State Name'] || 'N/A',
      'Issuing State Code': doc_details['Issuing State Code'] || 'N/A',
    }];
  }

  /**
   * Prepare document authentication data
   */
  prepare_document_authenticity() {
    const doc_auth = this.ocr.document_authenticity || {};
    
    return [{
      'Overall Status': checkStatusMap[doc_auth.overallStatus] || checkStatusMap[doc_auth.overall_status] || 'Unknown',
      'Optical Security': checkStatusMap[doc_auth.optical_security] || 'Unknown',
      'Text Authenticity': doc_auth.text_authenticity !== null ? checkStatusMap[doc_auth.text_authenticity] || 'Unknown' : 'Unknown',
      'Image Quality': doc_auth.image_quality !== null ? checkStatusMap[doc_auth.image_quality] || 'Unknown' : 'Unknown',
      'Doc Type Check': checkStatusMap[doc_auth.docType] || 'Unknown',
      'Expiry Check': checkStatusMap[doc_auth.expiry] || 'Unknown',
      'Image QA': checkStatusMap[doc_auth.imageQA] || 'Unknown',
      'MRZ Check': checkStatusMap[doc_auth.mrz] || 'Unknown',
      'Security Check': checkStatusMap[doc_auth.security] || 'Unknown',
      'Text Check': checkStatusMap[doc_auth.text] || 'Unknown',
      'Pages Count': doc_auth.pagesCount || 'N/A',
      'Checks': Array.isArray(doc_auth.checks) ? doc_auth.checks.join('; ') : 'N/A',
    }];
  }

  /**
   * Prepare facial comparison data (Selfie to Document)
   */
  prepare_facial_comparison_selfie_to_doc() {
    const fc = this.facial_comparison_data;
    const liveness = fc.selfie_liveness || {};
    const integrated = liveness.integrated || {};
    
    return [{
      'Is Match': fc.isMatch ? 'Yes' : 'No',
      'Match': fc.match ? 'Yes' : 'No',
      'Similarity': fc.similarity || 'N/A',
      'Cosine Distance': fc.cosine_distance || 'N/A',
      'Normalized Score': fc.normalized_score || 'N/A',
      'Confidence Level': fc.confidence_level || 'N/A',
      'Security Level': fc.security_level || 'N/A',
      'Comparison Type': fc.comparison_type || 'N/A',
      'Message': fc.message || 'N/A',
      'Recommendation': fc.recommendation || 'N/A',
      'Selfie Is Live': liveness.is_live ? 'Yes' : 'No',
      'Selfie Liveness Confidence': liveness.confidence || 'N/A',
      'Selfie Liveness Status': liveness.status || 'N/A',
      'Selfie Liveness Message': liveness.message || 'N/A',
      'Integrated Final Decision': integrated.final_decision || 'N/A',
      'Integrated Confidence': integrated.confidence || 'N/A',
      'Integrated Liveness Score': integrated.adjusted_liveness_score || 'N/A',
      'Quality Factor': integrated.quality_factor || 'N/A',
      'Screen Factor': integrated.screen_factor || 'N/A',
      'Selfie Quality Level': fc.selfie_quality?.qualityLevel || 'N/A',
      'Selfie Blur Score': fc.selfie_quality?.blurScore || 'N/A',
      'Selfie Sharpness': fc.selfie_quality?.sharpness || 'N/A',
      'Document Quality Level': fc.document_quality?.qualityLevel || 'N/A',
      'Document Blur Score': fc.document_quality?.blurScore || 'N/A',
      'Document Sharpness': fc.document_quality?.sharpness || 'N/A',
      'ISO 19795 Threshold': fc.iso_compliance?.iso_19795?.threshold_used || 'N/A',
      'ISO 19795 FAR': fc.iso_compliance?.iso_19795?.far_at_threshold || 'N/A',
      'ISO 19795 FRR': fc.iso_compliance?.iso_19795?.frr_at_threshold || 'N/A',
      'ISO 30107 Selfie Passed': fc.iso_compliance?.iso_30107?.selfie_passed ? 'Yes' : 'No',
    }];
  }

  /**
   * Prepare facial comparison data (DHA to Document)
   */
  prepare_facial_comparison_dha_to_doc() {
    const fc = this.dha_ocr_comparison_data;
    const liveness = fc.selfie_liveness || {};
    const integrated = liveness.integrated || {};
    
    return [{
      'Is Match': fc.isMatch ? 'Yes' : 'No',
      'Match': fc.match ? 'Yes' : 'No',
      'Similarity': fc.similarity || 'N/A',
      'Cosine Distance': fc.cosine_distance || 'N/A',
      'Normalized Score': fc.normalized_score || 'N/A',
      'Confidence Level': fc.confidence_level || 'N/A',
      'Security Level': fc.security_level || 'N/A',
      'Comparison Type': fc.comparison_type || 'N/A',
      'Message': fc.message || 'N/A',
      'Recommendation': fc.recommendation || 'N/A',
      'DHA Image Is Live': liveness.is_live ? 'Yes' : 'No',
      'DHA Liveness Confidence': liveness.confidence || 'N/A',
      'DHA Liveness Status': liveness.status || 'N/A',
      'DHA Liveness Message': liveness.message || 'N/A',
      'Integrated Final Decision': integrated.final_decision || 'N/A',
      'Integrated Confidence': integrated.confidence || 'N/A',
      'Integrated Liveness Score': integrated.adjusted_liveness_score || 'N/A',
      'Quality Factor': integrated.quality_factor || 'N/A',
      'Screen Factor': integrated.screen_factor || 'N/A',
      'DHA Quality Level': fc.selfie_quality?.qualityLevel || 'N/A',
      'DHA Blur Score': fc.selfie_quality?.blurScore || 'N/A',
      'DHA Sharpness': fc.selfie_quality?.sharpness || 'N/A',
      'Document Quality Level': fc.document_quality?.qualityLevel || 'N/A',
      'Document Blur Score': fc.document_quality?.blurScore || 'N/A',
      'Document Sharpness': fc.document_quality?.sharpness || 'N/A',
      'ISO 19795 Threshold': fc.iso_compliance?.iso_19795?.threshold_used || 'N/A',
      'ISO 19795 FAR': fc.iso_compliance?.iso_19795?.far_at_threshold || 'N/A',
      'ISO 19795 FRR': fc.iso_compliance?.iso_19795?.frr_at_threshold || 'N/A',
      'ISO 30107 DHA Passed': fc.iso_compliance?.iso_30107?.selfie_passed ? 'Yes' : 'No',
    }];
  }

  /**
   * Prepare liveness and quality assessment data
   */
  prepare_liveness_quality_assessment() {
    const selfie_liveness = this.facial_comparison_data.selfie_liveness || {};
    const quality = selfie_liveness.quality || {};
    const screen = selfie_liveness.screen || {};
    const liveness_det = selfie_liveness.liveness || {};
    
    return [{
      // Overall Quality
      'Overall Quality Score': quality.overall_score || 'N/A',
      'Quality Level': quality.quality_level || 'N/A',
      'Is Suitable': quality.is_suitable ? 'Yes' : 'No',
      'Face Size': quality.metrics?.face_size || 'N/A',
      'Quality Recommendations': Array.isArray(quality.recommendations) ? 
                                  quality.recommendations.join('; ') : 'N/A',
      
      // Screen Detection
      'Is Screen Likely': screen.is_screen_likely ? 'Yes' : 'No',
      'Screen Confidence': screen.confidence_score || 'N/A',
      'Screen Artifacts': Array.isArray(screen.detected_artifacts) ? 
                          screen.detected_artifacts.join('; ') : 'N/A',
      'Moire Score': screen.artifact_scores?.moire || 'N/A',
      'Pixel Patterns Score': screen.artifact_scores?.pixel_patterns || 'N/A',
      'Reflections Score': screen.artifact_scores?.reflections || 'N/A',
      'Screen Edges Score': screen.artifact_scores?.screen_edges || 'N/A',
      'Color Banding Score': screen.artifact_scores?.color_banding || 'N/A',
      'Compression Score': screen.artifact_scores?.compression || 'N/A',
      
      // Liveness Detection
      'Is Live': liveness_det.is_live ? 'Yes' : 'No',
      'Liveness Confidence': liveness_det.confidence_score || 'N/A',
      'Liveness Score': liveness_det.liveness_score || 'N/A',
      'Liveness Status': liveness_det.status || 'N/A',
      'Texture Analysis': liveness_det.detection_methods?.texture_analysis || 'N/A',
      'DeepFace AntiSpoof': liveness_det.detection_methods?.deepface_antispoof || 'N/A',
      'Laplacian': liveness_det.detection_methods?.laplacian || 'N/A',
      'Saturation': liveness_det.detection_methods?.saturation || 'N/A',
      'Edge Density': liveness_det.detection_methods?.edge_density || 'N/A',
      'Texture Score': liveness_det.detection_methods?.texture || 'N/A',
      'High Frequency': liveness_det.detection_methods?.high_freq || 'N/A',
      'Skin Color': liveness_det.detection_methods?.skin_color || 'N/A',
    }];
  }

  /**
   * Prepare name matching results
   */
  prepare_name_matching() {
    return [{
      'First Name Match': this.name_match.firstname_match ? 'Yes' : 'No',
      'First Name Score': this.name_match.firstname_score || 'N/A',
      'Last Name Match': this.name_match.lastname_match ? 'Yes' : 'No',
      'Last Name Score': this.name_match.lastname_score || 'N/A',
      'Overall Match': this.name_match.overall_match ? 'Yes' : 'No',
      'Warning': this.name_match.warning || 'N/A',
      'Input First Names': this.name_match.input_firstnames || 'N/A',
      'Input Last Name': this.name_match.input_lastname || 'N/A',
      'OCR First Names': this.name_match.ocr_firstnames || 'N/A',
      'OCR Last Name': this.name_match.ocr_lastname || 'N/A',
    }];
  }

  /**
   * Prepare raw JSON data
   */
  prepare_raw_json_data() {
    return {
      liveness: this.liveness,
      ocr: this.ocr,
      facial_comparison_data: this.facial_comparison_data,
      DHA_OCR_comparison_data: this.dha_ocr_comparison_data,
      name_match: this.name_match,
    };
  }
}
